import React, { useContext } from 'react';
import { FinancialData } from './types';
import { Check, Lock } from 'lucide-react';
import { UserContext } from '../../context/UserContext';

interface StandardFinancialTableProps {
    financialData: FinancialData;
    title?: string;
    selectedMetrics?: string[];
    onMetricToggle?: (metricId: string) => void;
}

interface RowConfig {
    id: string; // The metricName to look for in financialData
    label: string;
    indent?: number; // 0 = none, 1 = indented
    bold?: boolean;
    isTotal?: boolean; // Adds top border
    format?: 'currency' | 'percentage';
}

// Standard GAAP/IFRS Income Statement Structure
const INCOME_STATEMENT_TEMPLATE: RowConfig[] = [
    { id: 'totalRevenue', label: 'Total Revenue', bold: true },
    { id: 'costOfRevenue', label: 'Cost of Revenue', indent: 1 },
    { id: 'grossProfit', label: 'Gross Profit', bold: true, isTotal: true },

    { id: 'operatingExpenses', label: 'Operating Expenses', bold: true },
    { id: 'researchAndDevelopmentExpenses', label: 'Research & Development', indent: 1 },
    { id: 'sellingGeneralAndAdministrativeExpenses', label: 'Selling, General & Admin', indent: 1 },
    { id: 'otherOperatingExpenses', label: 'Other Operating Expenses', indent: 1 },

    { id: 'operatingIncome', label: 'Operating Income', bold: true, isTotal: true },

    { id: 'interestExpense', label: 'Interest Expense', indent: 1 },
    { id: 'interestIncome', label: 'Interest Income', indent: 1 },
    { id: 'otherNonOperatingIncome', label: 'Other Non-Operating Income', indent: 1 },

    { id: 'pretaxIncome', label: 'Pretax Income', bold: true, isTotal: true },

    { id: 'incomeTaxExpense', label: 'Income Tax Expense', indent: 1 },

    { id: 'netIncome', label: 'Net Income', bold: true, isTotal: true },

    { id: 'ebitda', label: 'EBITDA', bold: true },
    { id: 'ebit', label: 'EBIT', bold: true },
    { id: 'basicEPS', label: 'Basic EPS', bold: true },
    { id: 'dilutedEPS', label: 'Diluted EPS', bold: true },
    { id: 'eps', label: 'EPS', bold: true },
    { id: 'earningsPerShare', label: 'Earnings Per Share', bold: true },
    { id: 'epsDiluted', label: 'EPS Diluted', bold: true },
    { id: 'epsBasic', label: 'EPS Basic', bold: true },
];

// Standard Balance Sheet Structure
const BALANCE_SHEET_TEMPLATE: RowConfig[] = [
    // Assets
    { id: 'totalAssets', label: 'Total Assets', bold: true },
    { id: 'currentAssets', label: 'Current Assets', bold: true, indent: 1 },
    { id: 'cashAndCashEquivalents', label: 'Cash & Cash Equivalents', indent: 2 },
    { id: 'shortTermInvestments', label: 'Short-term Investments', indent: 2 },
    { id: 'netReceivables', label: 'Net Receivables', indent: 2 },
    { id: 'inventory', label: 'Inventory', indent: 2 },
    { id: 'otherCurrentAssets', label: 'Other Current Assets', indent: 2 },
    { id: 'totalCurrentAssets', label: 'Total Current Assets', bold: true, isTotal: true, indent: 1 },

    { id: 'nonCurrentAssets', label: 'Non-Current Assets', bold: true, indent: 1 },
    { id: 'propertyPlantEquipmentNet', label: 'Property, Plant & Equipment', indent: 2 },
    { id: 'goodwill', label: 'Goodwill', indent: 2 },
    { id: 'intangibleAssets', label: 'Intangible Assets', indent: 2 },
    { id: 'longTermInvestments', label: 'Long-term Investments', indent: 2 },
    { id: 'otherNonCurrentAssets', label: 'Other Non-Current Assets', indent: 2 },
    { id: 'totalNonCurrentAssets', label: 'Total Non-Current Assets', bold: true, isTotal: true, indent: 1 },

    // Liabilities
    { id: 'totalLiabilities', label: 'Total Liabilities', bold: true },
    { id: 'currentLiabilities', label: 'Current Liabilities', bold: true, indent: 1 },
    { id: 'accountPayables', label: 'Accounts Payable', indent: 2 },
    { id: 'shortTermDebt', label: 'Short-term Debt', indent: 2 },
    { id: 'deferredRevenue', label: 'Deferred Revenue', indent: 2 },
    { id: 'otherCurrentLiabilities', label: 'Other Current Liabilities', indent: 2 },
    { id: 'totalCurrentLiabilities', label: 'Total Current Liabilities', bold: true, isTotal: true, indent: 1 },

    { id: 'nonCurrentLiabilities', label: 'Non-Current Liabilities', bold: true, indent: 1 },
    { id: 'longTermDebt', label: 'Long-term Debt', indent: 2 },
    { id: 'deferredRevenueNonCurrent', label: 'Deferred Revenue (Non-Current)', indent: 2 },
    { id: 'deferredTaxLiabilitiesNonCurrent', label: 'Deferred Tax Liabilities', indent: 2 },
    { id: 'otherNonCurrentLiabilities', label: 'Other Non-Current Liabilities', indent: 2 },
    { id: 'totalNonCurrentLiabilities', label: 'Total Non-Current Liabilities', bold: true, isTotal: true, indent: 1 },

    // Equity
    { id: 'totalStockholderEquity', label: 'Total Stockholders Equity', bold: true },
    { id: 'commonStock', label: 'Common Stock', indent: 1 },
    { id: 'retainedEarnings', label: 'Retained Earnings', indent: 1 },
    { id: 'accumulatedOtherComprehensiveIncomeLoss', label: 'Accumulated Other Comprehensive Income', indent: 1 },
    { id: 'otherStockholderEquity', label: 'Other Stockholder Equity', indent: 1 },
];

// Standard Cash Flow Structure
const CASH_FLOW_TEMPLATE: RowConfig[] = [
    // Operating
    { id: 'netCashProvidedByOperatingActivities', label: 'Operating Cash Flow', bold: true },
    { id: 'netIncome', label: 'Net Income', indent: 1 },
    { id: 'depreciationAndAmortization', label: 'Depreciation & Amortization', indent: 1 },
    { id: 'deferredIncomeTax', label: 'Deferred Income Tax', indent: 1 },
    { id: 'stockBasedCompensation', label: 'Stock Based Compensation', indent: 1 },
    { id: 'changeInWorkingCapital', label: 'Change in Working Capital', indent: 1 },
    { id: 'accountsReceivables', label: 'Accounts Receivable', indent: 2 },
    { id: 'inventory', label: 'Inventory', indent: 2 },
    { id: 'accountsPayables', label: 'Accounts Payable', indent: 2 },
    { id: 'otherWorkingCapital', label: 'Other Working Capital', indent: 2 },
    { id: 'otherNonCashItems', label: 'Other Non-Cash Items', indent: 1 },

    // Investing
    { id: 'netCashUsedForInvestingActivities', label: 'Investing Cash Flow', bold: true },
    { id: 'investmentsInPropertyPlantAndEquipment', label: 'Capex', indent: 1 },
    { id: 'acquisitionsNet', label: 'Acquisitions Net', indent: 1 },
    { id: 'purchasesOfInvestments', label: 'Purchases of Investments', indent: 1 },
    { id: 'salesMaturitiesOfInvestments', label: 'Sales/Maturities of Investments', indent: 1 },
    { id: 'otherInvestingActivites', label: 'Other Investing Activities', indent: 1 },

    // Financing
    { id: 'netCashUsedProvidedByFinancingActivities', label: 'Financing Cash Flow', bold: true },
    { id: 'debtRepayment', label: 'Debt Repayment', indent: 1 },
    { id: 'commonStockIssued', label: 'Common Stock Issued', indent: 1 },
    { id: 'commonStockRepurchased', label: 'Common Stock Repurchased', indent: 1 },
    { id: 'dividendsPaid', label: 'Dividends Paid', indent: 1 },
    { id: 'otherFinancingActivites', label: 'Other Financing Activities', indent: 1 },

    // Summary
    { id: 'netChangeInCash', label: 'Net Change in Cash', bold: true, isTotal: true },
    { id: 'freeCashFlow', label: 'Free Cash Flow', bold: true },
];

export function StandardFinancialTable({
    financialData,
    title = "Income Statement",
    selectedMetrics = [],
    onMetricToggle,
    type = 'income'
}: StandardFinancialTableProps & { type?: 'income' | 'balance' | 'cashflow' }) {
    const periods = financialData.periods || [];
    const metrics = financialData.metrics || [];
    const { userData } = useContext(UserContext) || {};
    const isLoggedIn = typeof window !== 'undefined' && Boolean(sessionStorage.getItem('STOCK_AUTH_TOKEN'));
    const hasAccess = isLoggedIn && (userData?.planActive === true || userData?.subscriptionStatus === 'active' || userData?.subscriptionStatus === 'trialing');

    const unlockedCount = 10;
    const unlockedIndices = new Set(Array.from({ length: Math.min(unlockedCount, periods.length) }, (_, i) => i));

    const getTemplate = () => {
        switch (type) {
            case 'balance': return BALANCE_SHEET_TEMPLATE;
            case 'cashflow': return CASH_FLOW_TEMPLATE;
            default: return INCOME_STATEMENT_TEMPLATE;
        }
    };

    const template = getTemplate();

    // Helper to find metric data
    const getMetric = (id: string) => {
        // Try exact match, or case-insensitive, or without spaces
        return metrics.find(m =>
            m.metricName === id ||
            m.metricName.toLowerCase() === id.toLowerCase() ||
            m.metricName.replace(/\s+/g, '') === id.replace(/\s+/g, '')
        );
    };

    // Helper to get value for a specific period
    const getValue = (metric: any, period: string) => {
        if (!metric) return '-';
        const p = metric.periods.find((x: any) => x.year.year === period || x.year.yearStr === period);
        if (!p) return '-';

        const val = p.formattedValue || p.value;

        if (typeof val === 'object' && val !== null) {
            return val.formattedValue || val.value || '-';
        }

        return val;
    };

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
                <thead>
                    <tr>
                        <th className="py-3 px-4 bg-theme-secondary text-theme-primary font-semibold text-sm border-b border-theme-tertiary sticky left-0 z-10 w-64">
                            {title}
                        </th>
                        {periods.map((period, index) => (
                            <th
                                key={index}
                                className="py-3 px-4 bg-theme-secondary text-theme-secondary font-medium text-xs text-right border-b border-theme-tertiary whitespace-nowrap"
                            >
                                {period}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {template.map((row) => {
                        const metric = getMetric(row.id);

                        // If metric is missing, we might want to skip it to avoid empty rows,
                        // unless it's a major total which might indicate missing data.
                        // For now, let's skip if missing.
                        if (!metric) return null;

                        const isSelected = metric && selectedMetrics.includes(metric.metricName);

                        return (
                            <tr
                                key={row.id}
                                className={`
                  hover:bg-theme-tertiary/10 transition-colors
                  ${row.isTotal ? 'border-t border-theme-tertiary' : ''}
                `}
                            >
                                <td className={`
                  py-2 px-4 border-b border-theme-tertiary/50 sticky left-0 bg-theme-secondary z-10
                  text-sm text-theme-primary
                  ${row.bold ? 'font-bold' : 'font-normal'}
                `}>
                                    <div className="flex items-center">
                                        {onMetricToggle && (
                                            <div
                                                onClick={() => metric && onMetricToggle(metric.metricName)}
                                                className={`
                                                    shrink-0 mr-3 w-4 h-4 rounded flex items-center justify-center cursor-pointer border transition-colors
                                                    ${isSelected
                                                        ? 'bg-blue-600 border-blue-600'
                                                        : 'bg-theme-tertiary/30 border-theme-tertiary hover:border-theme-primary/50'}
                                                `}
                                            >
                                                {isSelected && <Check size={12} className="text-white" />}
                                            </div>
                                        )}
                                        <span className={`${row.indent ? 'pl-6' : ''}`}>
                                            {row.label}
                                        </span>
                                    </div>
                                </td>
                                {periods.map((period, index) => (
                                    <td
                                        key={index}
                                        className={`
                      py-2 px-4 border-b border-theme-tertiary/50 text-right text-sm
                      ${row.bold ? 'font-bold text-theme-primary' : 'text-theme-secondary'}
                    `}
                                    >
                                        {(!hasAccess && !unlockedIndices.has(index)) ? (
                                            <div className="flex justify-center">
                                                <Lock size={14} className="text-theme-tertiary" />
                                            </div>
                                        ) : (
                                            getValue(metric, period)
                                        )}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
